import { useEffect, useRef, useState } from "react";
import styles from "./style/SplitHourMinuteTimeInput.module.scss";

const digitsOnly = (raw, maxLen) =>
  String(raw).replace(/\D/g, "").slice(0, maxLen);

const SplitHourMinuteTimeInput = ({ value, onCommit, ariaLabel, disabled = false }) => {
  const [h, setH] = useState("");
  const [m, setM] = useState("");
  const hourInputRef = useRef(null);
  const minuteInputRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setH("");
      setM("");
      return;
    }
    const [vh, vm] = value.split(":");
    setH(vh ?? "");
    setM(vm ?? "");
  }, [value]);

  const flush = () => {
    const ht = digitsOnly(h, 2);
    const mt = digitsOnly(m, 2);
    setH(ht);
    setM(mt);
    if (!ht && !mt) {
      onCommit("");
      return;
    }
    const hn =
      ht === "" ? 0 : Math.min(23, Math.max(0, parseInt(ht, 10) || 0));
    const mn =
      mt === "" ? 0 : Math.min(59, Math.max(0, parseInt(mt, 10) || 0));
    const paddedH = String(hn).padStart(2, "0");
    const paddedM = String(mn).padStart(2, "0");
    setH(paddedH);
    setM(paddedM);
    onCommit(`${paddedH}:${paddedM}`);
  };

  const handlePartKeyDown = (part) => (e) => {
    const inputEl = e.currentTarget;
    const currentValue = inputEl.value || "";
    const selectionStart = inputEl.selectionStart ?? 0;
    const selectionEnd = inputEl.selectionEnd ?? selectionStart;
    const hasRangeSelection = selectionEnd > selectionStart;

    if (e.key === "Enter") {
      e.preventDefault();
      inputEl.blur();
      return;
    }

    if (
      e.key === "ArrowRight" &&
      part === "hour" &&
      !hasRangeSelection &&
      selectionStart >= currentValue.length
    ) {
      e.preventDefault();
      minuteInputRef.current?.focus();
      minuteInputRef.current?.setSelectionRange(0, 0);
      return;
    }

    if (
      e.key === "ArrowLeft" &&
      part === "minute" &&
      !hasRangeSelection &&
      selectionStart <= 0
    ) {
      e.preventDefault();
      hourInputRef.current?.focus();
      const hourLength = hourInputRef.current?.value?.length || 0;
      hourInputRef.current?.setSelectionRange(hourLength, hourLength);
    }
  };

  return (
    <div className={styles.box} role="group" aria-label={ariaLabel} aria-disabled={disabled}>
      <input
        ref={hourInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={styles.part}
        placeholder="--"
        value={h}
        disabled={disabled}
        aria-label={`${ariaLabel} 時`}
        onChange={(e) => setH(digitsOnly(e.target.value, 2))}
        onBlur={flush}
        onKeyDown={handlePartKeyDown("hour")}
      />
      <span className={styles.colon} aria-hidden>
        ：
      </span>
      <input
        ref={minuteInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={styles.part}
        placeholder="--"
        value={m}
        disabled={disabled}
        aria-label={`${ariaLabel} 分`}
        onChange={(e) => setM(digitsOnly(e.target.value, 2))}
        onBlur={flush}
        onKeyDown={handlePartKeyDown("minute")}
      />
    </div>
  );
};

export default SplitHourMinuteTimeInput;
